import React, { useState, useEffect } from "react";
import { useSeo } from "../seo";
import { Plus, Minus } from "lucide-react";
import { DynamicNavigation, Footer } from "../components";
import CTA02 from "../components/CTA02Section";
import { useNavLinkClick } from "../hooks/useNavLinkClick";

/* ─── Data ─────────────────────────────────────────────────────────────── */

const familyVisaCards = [
  {
    subclass: "Subclass 101 / 802",
    title: "Child Visa",
    desc: "Child visas allow dependent children of Australian citizens, permanent residents, or eligible New Zealand citizens to live permanently in Australia.",
    points: [
      "Subclass 101 — child is outside Australia",
      "Subclass 802 — child is already in Australia",
      "Children can live, study, and grow up in Australia",
    ],
    color: "indigo",
  },
  {
    subclass: "Subclass 804",
    title: "Aged Parent Visa",
    desc: "The Aged Parent visa allows older parents of Australian citizens or permanent residents to remain in Australia permanently if they meet age and eligibility requirements.",
    points: [
      "Must meet the balance of family test",
      "Permanent residence pathway",
      "For parents who meet age requirements",
    ],
    color: "violet",
  },
  {
    subclass: "Subclass 143",
    title: "Contributory Parent Visa",
    desc: "A permanent migration pathway for parents. This visa usually involves higher government charges but may offer shorter processing times than some other parent visa options.",
    points: [
      "Permanent residency for parents",
      "Higher government charges apply",
      "May offer shorter processing timeframes",
    ],
    color: "blue",
  },
  {
    subclass: "Subclass 870",
    title: "Sponsored Parent Visa",
    desc: "The Sponsored Parent visa allows parents to stay in Australia temporarily for extended periods — ideal for families wanting parents to spend more time in Australia.",
    points: [
      "Temporary stay for extended periods",
      "Sponsored by an eligible family member",
      "No immediate permanent migration required",
    ],
    color: "emerald",
  },
];

const partnerPathways = [
  {
    subclass: "Subclass 820 / 801",
    title: "Onshore Partner Visa",
    badge: "Already in Australia",
    desc: "For applicants who are already in Australia when they submit their partner visa application. Applicants may stay in Australia during processing if they hold a valid visa.",
    dark: false,
  },
  {
    subclass: "Subclass 309 / 100",
    title: "Offshore Partner Visa",
    badge: "Outside Australia",
    desc: "For applicants applying from outside Australia. If granted, this visa allows applicants to live in Australia with their partner.",
    dark: true,
  },
];

const requirements = [
  {
    title: "Relationship Evidence",
    desc: "Applicants must demonstrate their relationship with the sponsoring family member using documents such as marriage certificates, birth certificates, or other official records confirming the relationship.",
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" stroke="#4f46e5" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M14 2v6h6M16 13H8M16 17H8M10 9H8" stroke="#4f46e5" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
  },
  {
    title: "Sponsorship",
    desc: "Most family and partner visas require a sponsor who is an Australian citizen, permanent resident, or eligible New Zealand citizen. The sponsor usually agrees to support the applicant during their stay in Australia.",
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" stroke="#4f46e5" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
        <circle cx="9" cy="7" r="4" stroke="#4f46e5" strokeWidth="1.8"/>
        <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" stroke="#4f46e5" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
  },
  {
    title: "Genuine Relationship",
    desc: "Partner visa applicants must show the relationship is genuine and continuing. Evidence may include shared finances, joint living arrangements, communication records, and statements from friends or family.",
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
        <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" stroke="#4f46e5" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
  },
  {
    title: "Health & Character Requirements",
    desc: "Applicants are generally required to meet health examinations and character requirements set by the Australian Government before a visa can be granted.",
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
        <path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z" stroke="#4f46e5" strokeWidth="1.8"/>
        <path d="m9 12 2 2 4-4" stroke="#4f46e5" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
  },
];

const processSteps = [
  {
    step: "01",
    title: "Check Eligibility",
    desc: "Determine which visa pathway best suits your relationship and current location. Understanding the correct visa subclass is the first and most important step.",
    image: "/assets/services-cards/images/skills-assessment.jpg",
  },
  {
    step: "02",
    title: "Prepare Supporting Documents",
    desc: "Gather identity documents, evidence of relationships, health records, and all other required supporting materials before lodging your application.",
    image: "/assets/services-cards/images/employer-sponsored-visas.jpg",
  },
  {
    step: "03",
    title: "Submit the Application",
    desc: "Submit the visa application to the Department of Home Affairs for assessment. Processing times vary depending on the visa type and individual circumstances.",
    image: "/assets/services-cards/images/corporate-immigration.jpg",
  },
];

const faqs = [
  {
    q: "Who can apply for a partner visa in Australia?",
    a: "The spouse or de facto partner of an Australian citizen, permanent resident, or eligible New Zealand citizen can apply for a partner visa. Applicants must be in a genuine and ongoing relationship.",
  },
  {
    q: "Can I bring my child to Australia permanently?",
    a: "Yes. The Child Visa (Subclass 101 for applicants outside Australia, Subclass 802 for those already in Australia) allows eligible dependent children to live permanently in Australia with their family.",
  },
  {
    q: "What is the difference between 820 and 309 visas?",
    a: "The Subclass 820 (Onshore Partner Visa) is for applicants already in Australia when they apply. The Subclass 309 (Offshore Partner Visa) is for applicants who apply from outside Australia.",
  },
  {
    q: "Can parents move to Australia permanently?",
    a: "Yes. There are several parent visa options, including the Aged Parent Visa (Subclass 804) and the Contributory Parent Visa (Subclass 143), which offer permanent residence pathways for eligible parents.",
  },
  {
    q: "How long do family visa applications take?",
    a: "Processing times vary significantly depending on the visa type and individual circumstances. Some applications can take several months to years. Speaking with a migration specialist can help set realistic expectations.",
  },
  {
    q: "What proof is needed for a genuine relationship?",
    a: "Evidence may include shared financial records, joint tenancy documents, communication records, photos together, and statutory declarations from friends or family confirming the relationship is genuine and ongoing.",
  },
];

/* ─── Sub-components ────────────────────────────────────────────────────── */

const colorMap: Record<string, { badge: string; accent: string; dot: string }> = {
  indigo: { badge: "text-indigo-600 bg-indigo-50 border-indigo-100", accent: "from-indigo-500 to-blue-500", dot: "#4f46e5" },
  violet: { badge: "text-violet-600 bg-violet-50 border-violet-100", accent: "from-violet-500 to-purple-500", dot: "#7c3aed" },
  blue:   { badge: "text-blue-600 bg-blue-50 border-blue-100",     accent: "from-blue-500 to-cyan-500",   dot: "#2563eb" },
  emerald:{ badge: "text-emerald-600 bg-emerald-50 border-emerald-100", accent: "from-emerald-500 to-teal-500", dot: "#059669" },
};

const FamilyVisaCard = ({ subclass, title, desc, points, color }: (typeof familyVisaCards)[0]) => {
  const c = colorMap[color];
  return (
    <div className="group bg-white rounded-2xl border border-slate-200 hover:border-indigo-200 hover:shadow-xl transition-all duration-300 flex flex-col overflow-hidden">
      <div className={`h-1 w-full bg-gradient-to-r ${c.accent}`} />
      <div className="flex flex-col flex-1 p-6">
        <span className={`inline-block text-xs font-semibold border rounded-full px-3 py-1 mb-4 w-max ${c.badge}`}>
          {subclass}
        </span>
        <h3 className="text-[15px] font-bold text-gray-800 mb-2 leading-snug">{title}</h3>
        <p className="text-sm text-slate-500 leading-relaxed mb-4">{desc}</p>
        <ul className="mt-auto space-y-2">
          {points.map((p, i) => (
            <li key={i} className="flex items-center gap-2 text-sm text-slate-600">
              <span className="shrink-0 w-4 h-4 rounded-full flex items-center justify-center" style={{ background: `${c.dot}18` }}>
                <svg width="9" height="9" viewBox="0 0 12 12" fill="none">
                  <path d="M2 6l3 3 5-5" stroke={c.dot} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </span>
              {p}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

/* ─── Page ──────────────────────────────────────────────────────────────── */

export default function FamilyPartnerVisasAustralia() {
  const [openFaqIndex, setOpenFaqIndex] = useState(0);
  const [activeStep, setActiveStep] = useState(0);
  const [prevStep, setPrevStep] = useState<number | null>(null);
  const [slideDir, setSlideDir] = useState<"next" | "prev">("next");
  const [animating, setAnimating] = useState(false);

  const navigate = (dir: "next" | "prev") => {
    if (animating) return;
    const next =
      dir === "next"
        ? (activeStep + 1) % processSteps.length
        : (activeStep - 1 + processSteps.length) % processSteps.length;
    setSlideDir(dir);
    setPrevStep(activeStep);
    setActiveStep(next);
    setAnimating(true);
    setTimeout(() => { setPrevStep(null); setAnimating(false); }, 540);
  };

  const goTo = (idx: number) => {
    if (idx === activeStep || animating) return;
    setSlideDir(idx > activeStep ? "next" : "prev");
    setPrevStep(activeStep);
    setActiveStep(idx);
    setAnimating(true);
    setTimeout(() => { setPrevStep(null); setAnimating(false); }, 540);
  };

  useEffect(() => {
    const t = setInterval(() => navigate("next"), 3500);
    return () => clearInterval(t);
  }, [activeStep, animating]);

  const links = [
    { id: "home", label: "Home", href: "/" },
    { id: "services", label: "Services", href: "/services" },
    { id: "about", label: "About", href: "/about" },
    { id: "contact", label: "Contact", href: "/contact" },
  ];
  const onLinkClick = useNavLinkClick(links);

  useSeo({
    title: "Family & Partner Visas Australia | Partner, Child & Parent Visa Help | Hola World Migration",
    description:
      "Bring your loved ones to Australia with family and partner visas. Explore partner, child, parent, and sponsored parent visa pathways with expert guidance from Hola World Migration.",
    path: "/family-partner-visas-australia",
    breadcrumbs: [
      { name: "Home", url: "/" },
      { name: "Family & Partner Visas Australia", url: "/family-partner-visas-australia" },
    ],
    faqItems: faqs.map((f) => ({ question: f.q, answer: f.a })),
  });

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      <header className="w-full">
        <DynamicNavigation
          links={links}
          logo={<img src="/logo.png" alt="Hola World Migration" className="h-10 w-auto" />}
          showLabelsOnMobile={true}
          glowIntensity={6}
          onLinkClick={onLinkClick}
        />
      </header>

      <main className="w-full flex flex-col items-center max-w-full">

        {/* ── HERO ─────────────────────────────────────────────────────── */}
        <section className="bg-gradient-to-b from-[#F5F7FF] via-[#fffbee] to-[#E6EFFF] w-full font-poppins">
          <div className="flex flex-col md:flex-row items-center max-md:text-center justify-between mt-24 pb-16 px-6 sm:px-10 md:px-24 max-w-7xl mx-auto w-full gap-8">
            {/* Left */}
            <div className="flex flex-col items-center md:items-start">
              <button
                type="button"
                className="mt-16 mb-6 flex items-center gap-2 border border-indigo-600 text-indigo-600 text-xs rounded-full px-4 py-1.5 hover:bg-indigo-50 transition"
              >
                <span>Family & Partner Visa Pathways · Australia</span>
              </button>
              <h1
                className="text-gray-900 font-semibold text-4xl sm:text-5xl md:text-6xl max-w-xl leading-tight"
                style={{ fontFamily: "Poppins, sans-serif" }}
              >
                Family &amp; Partner Visas{" "}
                <span className="text-indigo-600">Australia</span>
              </h1>
              <p className="mt-4 text-gray-600 max-w-md text-sm sm:text-base leading-relaxed">
                Take the next step — bring your loved ones to Australia and start building your future together today.
              </p>
              <p className="mt-3 text-gray-500 max-w-md text-sm leading-relaxed">
                Australia provides visa pathways for eligible family members to join their loved ones.
              </p>

            </div>
            {/* Right: 2×2 image grid */}
            <div aria-label="Family visa images" className="mt-12 md:mt-0 grid grid-cols-2 gap-6 pb-6 shrink-0">
              <img alt="Family Visas" src="/assets/services-cards/images/family-partner-visas.jpg" loading="lazy" className="w-36 h-44 rounded-lg hover:scale-105 transition duration-300 object-cover shadow-lg" />
              <img alt="Partner Visas" src="/assets/services-cards/images/skilled-visas.jpg" loading="lazy" className="w-36 h-44 rounded-lg hover:scale-105 transition duration-300 object-cover shadow-lg" />
              <img alt="Migration" src="/assets/services-cards/images/employer-sponsored-visas.jpg" loading="lazy" className="w-36 h-44 rounded-lg hover:scale-105 transition duration-300 object-cover shadow-lg" />
              <img alt="Consultation" src="/assets/services-cards/images/corporate-immigration.jpg" loading="lazy" className="w-36 h-44 rounded-lg hover:scale-105 transition duration-300 object-cover shadow-lg" />
            </div>
          </div>
        </section>

        {/* ── SECTION 1: OVERVIEW ──────────────────────────────────────── */}
        <section className="w-full bg-white py-12 sm:py-16 px-4 sm:px-6 md:px-10 lg:px-24">
          <div className="w-full max-w-5xl mx-auto flex flex-col md:flex-row items-center justify-center gap-8 md:gap-12 lg:gap-16">
            <div className="relative rounded-2xl overflow-hidden shrink-0 w-full max-w-xs sm:max-w-sm md:max-w-md group">
              <img
                loading="lazy"
                className="w-full object-cover rounded-2xl aspect-square transition-transform duration-500 ease-in-out group-hover:scale-110"
                src="/assets/services-cards/images/family-partner-visas.jpg"
                alt="Family & Partner Visas Australia"
              />
            </div>
            <div className="w-full max-w-lg">
              <h2
                className="text-2xl sm:text-3xl md:text-4xl uppercase font-semibold text-slate-700"
                style={{ fontFamily: "Poppins, sans-serif" }}
              >
                Family &amp; Partner Visas
              </h2>
              <div className="w-24 h-[3px] rounded-full bg-gradient-to-r from-indigo-600 to-[#DDD9FF] mt-2 mb-6" />
              <p className="text-sm sm:text-base text-slate-600 leading-relaxed mb-4">
                Family and partner visas allow Australian citizens, permanent residents, and eligible New Zealand citizens to sponsor certain family members to live in Australia.
              </p>
              <p className="text-sm sm:text-base text-slate-600 leading-relaxed mb-4">
                These visas are designed to support family reunification by enabling partners, children, and parents to stay together in Australia — either temporarily or permanently, depending on the visa category.
              </p>
              <p className="text-sm sm:text-base text-slate-600 leading-relaxed">
                Choosing the correct visa pathway and preparing the required documentation are important steps when planning a family migration application.
              </p>
            </div>
          </div>
        </section>

        {/* ── SECTION 2: FAMILY VISA OPTIONS ──────────────────────────── */}
        <section className="w-full bg-blue-50 py-12 sm:py-16 px-4 sm:px-6 md:px-10 lg:px-24">
          <div className="w-full max-w-6xl mx-auto">
            <h2
              className="text-3xl sm:text-4xl font-semibold text-[#0a2540] mb-3"
              style={{ fontFamily: "Poppins, sans-serif" }}
            >
              Family Visa Options
            </h2>
            <div className="w-24 h-[3px] rounded-full bg-gradient-to-r from-indigo-600 to-[#DDD9FF] mb-5" />
            <p className="text-base text-slate-500 mb-10 max-w-3xl">
              Australia offers options for eligible family members to join their relatives. Below are the main family visa pathways available.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {familyVisaCards.map((card, i) => (
                <FamilyVisaCard key={i} {...card} />
              ))}
            </div>
          </div>
        </section>

        {/* ── SECTION 3: PARTNER VISAS ─────────────────────────────────── */}
        <section className="w-full bg-white py-12 sm:py-16 px-4 sm:px-6 md:px-10 lg:px-24">
          <div className="w-full max-w-5xl mx-auto flex flex-col md:flex-row-reverse items-center justify-center gap-8 md:gap-12 lg:gap-16">
            <div className="relative rounded-2xl overflow-hidden shrink-0 w-full max-w-xs sm:max-w-sm md:max-w-md group">
              <img
                loading="lazy"
                className="w-full object-cover rounded-2xl aspect-square transition-transform duration-500 ease-in-out group-hover:scale-110"
                src="/assets/services-cards/images/skilled-visas.jpg"
                alt="Partner Visas Australia"
              />
            </div>
            <div className="w-full max-w-lg">
              <h2
                className="text-2xl sm:text-3xl md:text-4xl uppercase font-semibold text-slate-700"
                style={{ fontFamily: "Poppins, sans-serif" }}
              >
                Partner Visas
              </h2>
              <div className="w-24 h-[3px] rounded-full bg-gradient-to-r from-indigo-600 to-[#DDD9FF] mt-2 mb-6" />
              <p className="text-sm sm:text-base text-slate-600 leading-relaxed mb-4">
                Partner visas allow the spouse or de facto partner of an Australian citizen, permanent resident, or eligible New Zealand citizen to live in Australia.
              </p>
              <p className="text-sm sm:text-base text-slate-600 leading-relaxed mb-4">
                These visas are intended for couples in genuine relationships who wish to live together in Australia. Applicants must provide evidence showing that the relationship is real and ongoing.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-6">
                {[
                  { label: "Genuine relationship evidence required", icon: "❤️" },
                  { label: "Includes spouse and de facto partners", icon: "👫" },
                  { label: "Temporary and permanent pathways", icon: "🏠" },
                  { label: "Pathway to Australian PR", icon: "✅" },
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3 bg-indigo-50 rounded-xl px-4 py-3 border border-indigo-100">
                    <span className="shrink-0 w-5 h-5 rounded-full bg-white border border-indigo-200 flex items-center justify-center">
                      <svg width="9" height="9" viewBox="0 0 12 12" fill="none">
                        <path d="M2 6l3 3 5-5" stroke="#4f46e5" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </span>
                    <span className="text-sm text-slate-700 font-medium">{item.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ── SECTION 4: PARTNER VISA PATHWAYS ────────────────────────── */}
        <section className="w-full bg-[#f8f9ff] py-12 sm:py-16 px-4 sm:px-6 md:px-10 lg:px-24">
          <div className="w-full max-w-6xl mx-auto">
            <h2
              className="text-3xl sm:text-4xl font-semibold text-[#0a2540] mb-3"
              style={{ fontFamily: "Poppins, sans-serif" }}
            >
              Partner Visa Pathways
            </h2>
            <div className="w-24 h-[3px] rounded-full bg-gradient-to-r from-indigo-600 to-[#DDD9FF] mb-5" />
            <p className="text-base text-slate-500 mb-10 max-w-3xl">
              There are two main partner visa pathways, depending on where the applicant is located at the time of application.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {partnerPathways.map((p, i) => (
                <div
                  key={i}
                  className={`rounded-2xl p-8 border transition-all duration-200 hover:shadow-lg ${
                    p.dark
                      ? "bg-[#0a2540] border-[#0a2540] text-white"
                      : "bg-white border-slate-200 hover:border-indigo-200"
                  }`}
                >
                  <div className="flex items-start justify-between mb-5">
                    <span className={`inline-block text-xs font-semibold border rounded-full px-3 py-1 ${
                      p.dark
                        ? "text-indigo-300 bg-white/10 border-white/20"
                        : "text-indigo-600 bg-indigo-50 border-indigo-100"
                    }`}>
                      {p.subclass}
                    </span>
                    <span className={`inline-block text-xs font-semibold px-3 py-1 rounded-full ${
                      p.dark ? "bg-white/10 text-white/70" : "bg-amber-50 text-amber-700 border border-amber-200"
                    }`}>
                      {p.badge}
                    </span>
                  </div>
                  <h3 className={`text-xl font-bold mb-3 leading-snug ${p.dark ? "text-white" : "text-gray-800"}`}>
                    {p.title}
                  </h3>
                  <p className={`text-sm leading-relaxed ${p.dark ? "text-slate-300" : "text-slate-500"}`}>
                    {p.desc}
                  </p>
                  <div className={`mt-6 pt-5 border-t ${p.dark ? "border-white/10" : "border-slate-100"}`}>
                    <a
                      href="/contact?from=family-visas-page-cta"
                      className={`inline-flex items-center gap-1.5 text-sm font-semibold transition-colors ${
                        p.dark ? "text-indigo-300 hover:text-white" : "text-indigo-600 hover:text-indigo-800"
                      }`}
                    >
                      Learn More
                      <svg width="13" height="12" viewBox="0 0 13 12" fill="none">
                        <path d="M12.53 6.53a.75.75 0 0 0 0-1.06L7.757.697a.75.75 0 1 0-1.06 1.06L10.939 6l-4.242 4.243a.75.75 0 0 0 1.06 1.06zM0 6v.75h12v-1.5H0z" fill="currentColor"/>
                      </svg>
                    </a>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── SECTION 5: REQUIREMENTS ──────────────────────────────────── */}
        <section className="w-full bg-gray-50 py-16 px-4 sm:px-6 md:px-10 lg:px-24">
          <div className="w-full max-w-6xl mx-auto">
            <h2
              className="text-3xl sm:text-4xl font-semibold text-[#0a2540] mb-3"
              style={{ fontFamily: "Poppins, sans-serif" }}
            >
              General Requirements
            </h2>
            <div className="w-24 h-[3px] rounded-full bg-gradient-to-r from-indigo-600 to-[#DDD9FF] mb-5" />
            <p className="text-base text-slate-500 mb-10 max-w-3xl">
              Although each visa type has its own eligibility criteria, some common requirements apply to many family and partner visa applications.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {requirements.map((req, i) => (
                <div
                  key={i}
                  className="bg-white rounded-2xl border border-slate-200 hover:border-indigo-200 hover:shadow-md transition-all duration-200 p-6 flex flex-col gap-4"
                >
                  <div className="w-12 h-12 rounded-xl bg-indigo-50 flex items-center justify-center shrink-0">
                    {req.icon}
                  </div>
                  <h3 className="text-base font-bold text-gray-800 leading-snug">{req.title}</h3>
                  <p className="text-sm text-slate-500 leading-relaxed flex-1">{req.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── SECTION 6: PROCESS STEPPER ───────────────────────────────── */}
        <section className="w-full relative overflow-hidden" style={{ background: "#1B31F5" }}>
          <div className="max-w-6xl mx-auto px-6 sm:px-10 md:px-16 pt-16 pb-0 relative">
            <div
              aria-hidden="true"
              className="absolute inset-0 flex items-center justify-end pr-4 sm:pr-20 pointer-events-none select-none z-0 overflow-hidden"
            >
              <span
                key={`num-${activeStep}`}
                className="animate-process-num text-[320px] sm:text-[420px] md:text-[520px] font-black leading-none"
                style={{ color: "rgba(255,255,255,0.13)", letterSpacing: "-0.04em" }}
              >
                {processSteps[activeStep].step}
              </span>
            </div>
            <h2
              className="relative z-10 text-white font-bold text-3xl sm:text-4xl md:text-5xl text-center mb-12"
              style={{ fontFamily: "Poppins, sans-serif" }}
            >
              How the Application Process Works
            </h2>
            <div className="relative z-10 flex flex-col lg:flex-row items-center justify-between gap-12">
              {/* Left: step info */}
              <div
                key={activeStep}
                className={`flex-1 max-w-lg ${prevStep !== null ? (slideDir === "next" ? "proc-step-enter-right" : "proc-step-enter-left") : ""}`}
              >
                <p className="text-white/50 text-xs uppercase tracking-[0.2em] font-semibold mb-3">
                  Step {processSteps[activeStep].step}
                </p>
                <h3 className="text-white font-bold text-3xl sm:text-4xl mb-4 leading-snug">
                  {processSteps[activeStep].title}
                </h3>
                <p className="text-white/80 text-base sm:text-lg leading-relaxed mb-10">
                  {processSteps[activeStep].desc}
                </p>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => navigate("prev")}
                    className="w-12 h-12 rounded-full bg-white/10 hover:bg-white/25 border border-white/25 flex items-center justify-center transition"
                    aria-label="Previous step"
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                      <path d="M15 18l-6-6 6-6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </button>
                  <button
                    onClick={() => navigate("next")}
                    className="w-12 h-12 rounded-full bg-white/10 hover:bg-white/25 border border-white/25 flex items-center justify-center transition"
                    aria-label="Next step"
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                      <path d="M9 18l6-6-6-6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </button>
                </div>
              </div>
              {/* Right: image */}
              <div className="flex-shrink-0 flex items-end justify-center lg:justify-end mb-14">
                <div className="relative w-80 h-64 sm:w-[440px] sm:h-80">
                  {prevStep !== null && prevStep < processSteps.length && (
                    <img
                      src={processSteps[prevStep].image}
                      alt=""
                      aria-hidden="true"
                      className={`absolute inset-0 w-full h-full object-cover rounded-2xl shadow-2xl shadow-black/40 ${slideDir === "next" ? "proc-img-exit-left" : "proc-img-exit-right"}`}
                    />
                  )}
                  <img
                    key={activeStep}
                    src={processSteps[activeStep].image}
                    alt={processSteps[activeStep].title}
                    className={`absolute inset-0 w-full h-full object-cover rounded-2xl shadow-2xl shadow-black/40 ${prevStep !== null ? (slideDir === "next" ? "proc-img-enter-right" : "proc-img-enter-left") : ""}`}
                  />
                </div>
              </div>
            </div>
          </div>
          {/* Timeline dots */}
          <div className="relative z-10 max-w-6xl mx-auto px-6 sm:px-10 md:px-16">
            <div className="flex items-start gap-0 overflow-x-auto pb-12 scrollbar-hide">
              {processSteps.map((s, i) => (
                <button
                  key={i}
                  onClick={() => goTo(i)}
                  className={`flex flex-col items-start flex-1 min-w-[90px] pr-3 transition-opacity duration-300 ${activeStep === i ? "opacity-100" : "opacity-40 hover:opacity-65"}`}
                >
                  <div className="relative w-full flex items-center mb-4" style={{ height: "14px" }}>
                    <div className="absolute left-0 right-0 top-1/2 -translate-y-1/2 h-px bg-white/25" />
                    <span className={`relative z-10 block rounded-full border-2 transition-all duration-300 shrink-0 ${activeStep === i ? "w-3.5 h-3.5 bg-white border-white shadow-[0_0_8px_2px_rgba(255,255,255,0.45)]" : "w-2 h-2 bg-white/30 border-white/40"}`} />
                  </div>
                  <span className={`font-bold text-base sm:text-lg leading-none ${activeStep === i ? "text-white" : "text-white/70"}`}>{s.step}</span>
                  <span className={`text-xs mt-1.5 leading-snug text-left ${activeStep === i ? "text-white font-semibold" : "text-white/55"}`}>{s.title}</span>
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* ── SECTION 7: SUPPORT FROM HOLA WORLD ───────────────────────── */}
        <section className="w-full bg-white py-16 px-4 sm:px-6 md:px-10 lg:px-24">
          <div className="w-full max-w-5xl mx-auto flex flex-col md:flex-row items-center gap-12">
            <div className="w-full max-w-lg">
              <h2
                className="text-3xl sm:text-4xl font-semibold text-[#0a2540] mb-4"
                style={{ fontFamily: "Poppins, sans-serif" }}
              >
                Support from Hola World Migration
              </h2>
              <div className="w-24 h-[3px] rounded-full bg-gradient-to-r from-indigo-600 to-[#DDD9FF] mb-7" />
              <p className="text-base text-slate-600 leading-relaxed mb-4">
                Family and partner visa applications often require detailed documentation and careful preparation.
              </p>
              <p className="text-base text-slate-600 leading-relaxed mb-4">
                Hola World Migration provides guidance to help applicants understand available visa pathways and organise the documents needed for their application.
              </p>
              <p className="text-base text-slate-600 leading-relaxed">
                Understanding the process and preparing a strong application can help applicants move forward with greater confidence.
              </p>
            </div>
            <div className="relative rounded-2xl overflow-hidden shrink-0 w-full max-w-xs sm:max-w-sm group">
              <img
                loading="lazy"
                className="w-full object-cover rounded-2xl aspect-square transition-transform duration-500 ease-in-out group-hover:scale-110"
                src="/assets/services-cards/images/corporate-immigration.jpg"
                alt="Hola World Migration family visa support"
              />
            </div>
          </div>
        </section>

        {/* ── FAQ ──────────────────────────────────────────────────────── */}
        <section className="w-full bg-gray-50 py-24 px-4 sm:px-6 md:px-10 lg:px-24">
          <div className="w-full max-w-5xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
              {/* Left sticky */}
              <div className="lg:sticky lg:top-24">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-50 border border-blue-200 mb-6">
                  <span className="text-xs font-bold uppercase tracking-wider text-blue-600">Self Service Help</span>
                </div>
                <h2
                  className="text-4xl md:text-5xl font-black text-[#0a2540] mb-8"
                  style={{ fontFamily: "Poppins, sans-serif", lineHeight: "1.1" }}
                >
                  Frequently Asked <br /> Questions
                </h2>
                <p className="text-base text-gray-600 max-w-sm mb-6 leading-relaxed">
                  Have questions about family and partner visas? Find answers to commonly asked questions below.
                </p>
                <div className="rounded-2xl overflow-hidden w-full max-w-sm">
                  <img
                    src="/assets/faq/faq-image.png"
                    alt="Family Visa FAQ"
                    loading="lazy"
                    className="w-full h-auto object-contain"
                  />
                </div>
              </div>
              {/* Right: accordion */}
              <div className="space-y-4">
                {faqs.map((faq, i) => (
                  <div
                    key={i}
                    className="rounded-xl border border-gray-200 bg-white hover:border-blue-300 transition-colors"
                  >
                    <button
                      onClick={() => setOpenFaqIndex(openFaqIndex === i ? -1 : i)}
                      className="w-full flex items-center justify-between p-6 text-left hover:bg-gray-50 transition-colors"
                      aria-expanded={openFaqIndex === i}
                    >
                      <span className="text-base sm:text-lg font-semibold text-[#0a2540] pr-4">{faq.q}</span>
                      <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0 bg-gray-100">
                        {openFaqIndex === i ? (
                          <Minus className="w-4 h-4 text-gray-600" />
                        ) : (
                          <Plus className="w-4 h-4 text-gray-600" />
                        )}
                      </div>
                    </button>
                    {openFaqIndex === i && (
                      <div className="px-6 pb-6">
                        <div className="h-px bg-gray-200 mb-6" />
                        <p className="text-base text-gray-700 leading-relaxed">{faq.a}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ── DISCLAIMER ───────────────────────────────────────────────── */}
        <div className="py-8 px-6 md:px-24 bg-white border-t border-gray-100 w-full">
          <p className="text-xs text-gray-400 leading-relaxed max-w-5xl mx-auto">
            <strong className="text-gray-500">Disclaimer:</strong> The information on this page is intended as general guidance only and does not constitute legal, migration, or professional advice. Visa requirements, processing times, and eligibility criteria are subject to change by the Australian Government without notice. Always verify current requirements directly with the Department of Home Affairs or consult a registered migration agent (MARA) before making decisions about your migration pathway.
          </p>
        </div>

        <CTA02 />
      </main>

      <Footer />
    </div>
  );
}
